import { useCallback } from 'react';
import { css } from '@emotion/react';
import dayjs from 'dayjs';
import { AnalyticsPlatformV1 } from '@frontend/api-analytics-platform';
import { AnalyticsHooks } from '@frontend/analytics-shared';
import { getUser } from '@frontend/auth-helpers';
import { useTranslation } from '@frontend/i18n';
import { useIsCompactMode } from '@frontend/responsiveness';
import { useAppScopeStore } from '@frontend/scope';
import { SurfaceHeader } from '@frontend/surface-header';
import { theme } from '@frontend/theme';
import { Button, TextField, useAlert, useControlledField } from '@frontend/design-system';
import { METRICS_OPTIONS } from '../constants';
import { LastSavedConfig, useAnalyticsReportingShallowStore } from '../hooks';
import { getSaveConfigKey } from '../utils';
import { DownloadInProgress } from './download-in-progress';
import { Filters } from './filters';
import { Metrices } from './metrices';

interface ExportContentProps {
  closeModal: () => void;
}

export const ExportContent = ({ closeModal }: ExportContentProps) => {
  const alert = useAlert();
  const { t } = useTranslation('analytics-reporting');
  const isCompactMode = useIsCompactMode();
  const { downloadStatus, fileName, filters, onDownloadStatusChange, onFileNameChange, selectedMetrices } =
    useAnalyticsReportingShallowStore(
      'downloadStatus',
      'fileName',
      'filters',
      'onDownloadStatusChange',
      'onFileNameChange',
      'selectedMetrices'
    );
  const { selectedLocationIds } = useAppScopeStore();

  const titleField = useControlledField({
    type: 'text',
    onChange: (value) => {
      onFileNameChange(value);
    },
    value: fileName,
  });

  const { isLoading: isRequestingDownload, mutateAsync: requestDownload } =
    AnalyticsPlatformV1.Mutations.useHandleGetReportsWithFormatMutation({
      options: {
        onSuccess: ({ downloadUrl, fileName, hash, status, outOfBoundsMetrics }) => {
          if (outOfBoundsMetrics && outOfBoundsMetrics.length > 0) {
            const outOfBoundsMetricLabels = METRICS_OPTIONS.reduce((acc, option) => {
              if (outOfBoundsMetrics.includes(option.id)) {
                acc.push(option.label);
              }
              return acc;
            }, [] as string[]);
            const formattedMetrics =
              outOfBoundsMetricLabels.length > 1
                ? `${outOfBoundsMetricLabels.slice(0, -1).join(', ')} ${t('and')} ${
                    outOfBoundsMetricLabels[outOfBoundsMetricLabels.length - 1]
                  }`
                : outOfBoundsMetricLabels[0];
            alert.error(
              t('{{metrics}} data is not available for the selected criteria.', {
                metrics: formattedMetrics,
              })
            );
          }

          if (!downloadUrl || !fileName || !hash) {
            return;
          }

          onDownloadStatusChange(
            {
              downloadUrl,
              fileName,
              isPreparingDownload: status !== 'completed',
              mediaHash: hash,
            },
            selectedLocationIds
          );
        },
        onError: () => {
          alert.error(t("Couldn't generate the report. Please try again."));
        },
      },
    });

  const disabled =
    isRequestingDownload ||
    downloadStatus?.isPreparingDownload ||
    (fileName ?? '').trim().length === 0 ||
    !filters?.startDate ||
    !filters?.endDate ||
    filters.locationIds?.length === 0 ||
    selectedMetrices?.length === 0;

  const handleSave = useCallback(() => {
    const userId = getUser()?.userID;
    if (!userId) {
      return;
    }

    // For time period filter, get the selected key else save start and end dates
    const timePeriodKey =
      filters?.startDate && filters?.endDate
        ? AnalyticsHooks.findExistingTimePeriodKey(filters.startDate, filters.endDate)
        : '';

    const configToSave: LastSavedConfig = {
      fileName,
      filters,
      selectedMetrices,
      timePeriodKey,
    };

    localStorage.setItem(
      getSaveConfigKey({
        glsLocationIds: selectedLocationIds,
        userId,
      }),
      JSON.stringify(configToSave)
    );
    alert.success(t('Your report configuration has been saved. This will be used as default next time.'));
  }, [alert, fileName, filters, selectedLocationIds, selectedMetrices, t]);

  const handleDownload = useCallback(() => {
    requestDownload({
      filters: {
        startTime: dayjs(filters?.startDate).format('YYYY-MM-DDT00:00:00.000'),
        endTime: dayjs(filters?.endDate).format('YYYY-MM-DDT23:59:59.999'),
      },
      format: 'csv',
      locationId: filters?.locationIds,
      metricName: selectedMetrices,
      reportName: fileName,
    });
  }, [filters?.endDate, filters?.locationIds, fileName, filters?.startDate, requestDownload, selectedMetrices]);

  return (
    <>
      <SurfaceHeader.Root>
        <SurfaceHeader.Row>
          {isCompactMode && (
            <SurfaceHeader.Start>
              <SurfaceHeader.Icon
                iconName='caret-left-small'
                onClick={closeModal}
                trackingId='analytics-reporting-close-tray'
              />
            </SurfaceHeader.Start>
          )}
          <SurfaceHeader.Title>{t('Generate Report')}</SurfaceHeader.Title>
          {!isCompactMode && (
            <SurfaceHeader.Actions>
              <SurfaceHeader.Icon iconName='x' onClick={closeModal} trackingId='analytics-reporting-close-tray' />
            </SurfaceHeader.Actions>
          )}
        </SurfaceHeader.Row>
      </SurfaceHeader.Root>
      <div className='report-select-content' css={styles.container}>
        {downloadStatus?.isPreparingDownload ? (
          <DownloadInProgress />
        ) : (
          <>
            <TextField
              {...titleField}
              data-trackingid='analytics-reporting-report-name'
              label={t('Report Name')}
              name='report-name'
            />
            <Filters />
            <Metrices />
          </>
        )}
      </div>
      <footer css={[styles.container, styles.actionsBar]}>
        <Button
          disabled={disabled}
          onClick={handleSave}
          size='large'
          trackingId='analytics-reporting-save-config'
          variant='secondary'
        >
          {t('Save')}
        </Button>
        <Button
          disabled={disabled}
          iconName='download-small'
          loading={isRequestingDownload}
          onClick={handleDownload}
          size='large'
          style={{ minWidth: 125 }}
          trackingId='analytics-reporting-download-report'
          variant='primary'
        >
          {t('Download')}
        </Button>
      </footer>
    </>
  );
};

const styles = {
  tray: css`
    gap: 0;
    padding: 0;
  `,

  container: css`
    border-bottom: 1px solid ${theme.colors.neutral20};
    padding: ${theme.spacing(2, 3)};

    &.report-select-content {
      flex: 1;
      overflow-y: auto;
      display: flex;
      flex-direction: column;
      gap: ${theme.spacing(3)};
      padding-top: ${theme.spacing(3)};
    }
  `,

  actionsBar: css`
    display: flex;
    gap: ${theme.spacing(1)};
    justify-content: flex-end;

    svg {
      height: ${theme.spacing(2.5)};
      min-height: ${theme.spacing(2.5)};
      min-width: ${theme.spacing(2.5)};
      width: ${theme.spacing(2.5)};
    }
  `,
};
